import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiGhostPanel, uiHeader, uiKpis, uiMoney, uiPct, uiPill, uiPlanDrawer, uiTrendChart } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Banking & Debt — redesigned (replaces app.js renderBanking).
 *
 * A debt desk rather than two tables: what you owe and what it costs each
 * month, every loan's repayment progress, refinancing opportunities with
 * their payback, your credit rating and what moves it, how the debt runs
 * down, and each lender's terms and covenant headroom. Borrowing opens a
 * drawer that previews the exact payment, total interest and new rating.
 * Data: /api/debt-desk, /api/loans/preview, /api/loans/take|refinance, /api/commands/pay-down-debt.
 */

function bkRatingTone(r) {
  if (!r) return "";
  if (r.startsWith("AAA") || r.startsWith("AA")) return "good";
  if (r.startsWith("A") || r.startsWith("BBB")) return "info";
  if (r.startsWith("BB")) return "warn";
  return "bad";
}

function bkLoanRow(l) {
  return `
    <div class="bk-loan">
      <div class="bk-loan-top">
        <div>
          <div class="bk-loan-name">${esc(l.lender)} ${l.is_emergency ? uiPill("Emergency bridge", "bad") : uiPill(l.account === "ST_DEBT" ? "Short-term" : "Term loan", "")}</div>
          <div class="bk-loan-meta">${uiPct(l.rate * 100, 1)} · ${l.term_months}-month term · taken M${l.taken_month} · ${l.months_left} payments left</div>
        </div>
        <div class="bk-loan-amt">
          <strong>${uiMoney(l.outstanding)}</strong>
          <span>of ${uiMoney(l.principal, { compact: true })}</span>
        </div>
      </div>
      <div class="bk-progress" title="${l.repaid_pct}% repaid"><div style="width:${Math.min(100, Math.max(0, l.repaid_pct))}%"></div></div>
      <div class="bk-loan-foot">
        <span>Pays <strong>${uiMoney(l.monthly_payment)}</strong>/mo · ${uiMoney(l.interest_next_month)} of it interest next month</span>
        <span class="bk-loan-actions">
          ${l.refinance ? `<button type="button" class="${l.refinance.worth_it ? "primary" : ""}" data-refi="${l.id}" data-bank="${esc(l.refinance.bank)}"
            title="Saves ${uiMoney(l.refinance.annual_saving)}/yr for a ${uiMoney(l.refinance.fee)} fee">
            Refinance at ${uiPct(l.refinance.rate * 100, 1)}${l.refinance.payback_months ? ` · pays back in ${Math.ceil(l.refinance.payback_months)} mo` : ""}</button>` : ""}
          <button type="button" data-payoff="${l.id}" data-amount="${l.outstanding}">Pay off</button>
        </span>
      </div>
    </div>`;
}

function bkLenderCard(q, dte, best) {
  const used = Math.min(100, (dte / q.max_debt_to_equity) * 100);
  return `
    <div class="bk-lender ${q.eligible ? "" : "off"} ${best ? "best" : ""}">
      ${best ? `<span class="bk-best">Best rate for you</span>` : ""}
      <div class="bk-lender-top"><div><strong>${esc(q.bank_name)}</strong><div class="bk-sub">${esc(q.blurb || "")}</div></div>${uiPill(q.kind || "Bank", "info")}</div>
      <div class="bk-lender-rate"><b>${uiPct(q.quoted_rate * 100, 2)}</b><span>a year for you</span>
        ${(q.rate_notes || []).map((n) => uiPill(n, /discount|favours/.test(n) ? "good" : "warn")).join(" ")}</div>
      ${q.eligible ? `<div class="bk-sub">Will lend ${uiMoney(q.min_loan, { compact: true })}–${uiMoney(q.max_loan, { compact: true })}</div>
        <div class="bk-covenant"><div class="${used >= 85 ? "hot" : ""}" style="width:${used}%"></div></div>
        <div class="bk-sub">Covenant ${q.max_debt_to_equity}x debt/equity · you're at ${dte.toFixed(2)}x${q.macro_rate_addon > 0 ? ` · +${uiPct(q.macro_rate_addon * 100, 2)} from the central bank` : ""}</div>
        ${q.max_loan >= q.min_loan ? `<button type="button" class="${best ? "primary" : ""}" data-borrow="${esc(q.bank_name)}">Borrow</button>` : ""}`
      : `<div class="bk-why">${esc(q.why_not || "Declines right now.")}</div>`}
    </div>`;
}

function bkLenderRow(q, dte) {
  const used = Math.min(100, (dte / q.max_debt_to_equity) * 100);
  return `
    <tr>
      <td><strong>${esc(q.bank_name)}</strong>${q.macro_rate_addon > 0 ? `<div class="bk-sub">+${uiPct(q.macro_rate_addon * 100, 2)} from the central bank rate</div>` : ""}</td>
      <td class="num">${uiPct(q.quoted_rate * 100, 1)}</td>
      <td class="num">${q.eligible ? uiMoney(q.max_loan, { compact: true }) : "—"}</td>
      <td style="min-width:150px;">
        <div class="bk-covenant"><div class="${used >= 85 ? "hot" : ""}" style="width:${used}%"></div></div>
        <div class="bk-sub">Covenant ${q.max_debt_to_equity}x · you're at ${dte.toFixed(2)}x</div>
      </td>
      <td class="num">${q.eligible ? uiPill("Will lend", "good") : uiPill("Declines", "bad")}</td>
      <td style="text-align:right;">${q.eligible && q.max_loan >= q.min_loan ? `<button type="button" data-borrow="${esc(q.bank_name)}">Borrow</button>` : ""}</td>
    </tr>`;
}

function bkBorrowDrawer(desk, bankName) {
  const q = desk.quotes.find((x) => x.bank_name === bankName);
  const def = Math.round(Math.min(q.max_loan, Math.max(q.min_loan, desk.totals.cash * 0.2 || 100000)) / 1000) * 1000;
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal sx-drawer" role="dialog" aria-label="Borrow from ${esc(bankName)}">
      <div>
        <div class="sx-eyebrow">Borrow · ${uiPct(q.quoted_rate * 100, 1)} a year</div>
        <h3>Loan from ${esc(bankName)}</h3>
        <p class="sx-drawer-desc">Cash arrives today. You repay a fixed amount every month — part interest, part principal — until the loan is gone. Missing the covenant (${q.max_debt_to_equity}x debt/equity) closes this bank to you.</p>
      </div>
      <div>
        <label class="sx-field-label">Amount</label>
        <div class="sx-slider-row">
          <input type="range" id="bk-amt-range" min="${q.min_loan}" max="${q.max_loan}" step="1000" value="${def}">
          <input type="hidden" id="bk-amt" value="${def}">
          <b class="sx-slider-val" id="bk-amt-val">${uiMoney(def)}</b>
        </div>
        <label class="sx-field-label" style="margin-top:12px;">Term</label>
        <div class="bk-terms">${desk.term_options.map((t) => `<button type="button" class="bk-term${t === 36 ? " active" : ""}" data-term="${t}">${t} months</button>`).join("")}</div>
        <div class="sx-forecast" id="bk-preview"></div>
        <div class="sx-forecast" id="bk-ghost"></div>
      </div>
      <div class="modal-actions"><button type="button" id="bk-cancel">Cancel</button><button type="button" class="primary" id="bk-go">Take the loan</button></div>
    </div>`;
  document.body.appendChild(backdrop);
  const range = backdrop.querySelector("#bk-amt-range"), amt = backdrop.querySelector("#bk-amt");
  let term = 36, seq = 0, timer = null;
  const ghost = uiGhostPanel(backdrop.querySelector("#bk-ghost"));
  const preview = () => {
    ghost.run({ type: "take_loan_from_bank", params: { bank_name: bankName, amount: parseFloat(amt.value), term_months: term } });
    clearTimeout(timer);
    timer = setTimeout(async () => {
      const mine = ++seq;
      const host = backdrop.querySelector("#bk-preview");
      try {
        const p = await postJSON("/loans/preview", { bank_name: bankName, amount: parseFloat(amt.value), term_months: term });
        if (mine !== seq) return;
        host.innerHTML = `
          <div class="sx-fc-title">What this loan means</div>
          <div class="sx-fc-grid">
            <div class="sx-fc-stat"><span>Monthly payment</span><strong>${uiMoney(p.monthly_payment)}</strong></div>
            <div class="sx-fc-stat"><span>Total interest</span><strong class="neg">${uiMoney(p.total_interest)}</strong></div>
            <div class="sx-fc-stat"><span>All debt payments</span><strong>${uiMoney(p.new_monthly_debt_service)}/mo</strong></div>
            <div class="sx-fc-stat"><span>Debt/equity</span><strong class="${p.new_debt_to_equity > p.covenant_max * 0.85 ? "neg" : ""}">${p.new_debt_to_equity.toFixed(2)}x</strong></div>
            <div class="sx-fc-stat"><span>Credit rating</span><strong>${esc(p.rating_now)} → ${esc(p.rating_after)}</strong></div>
          </div>`;
      } catch (e) {
        if (mine === seq) host.innerHTML = `<div class="sx-forecast-error">${esc(e.message)}</div>`;
      }
    }, 250);
  };
  range.addEventListener("input", () => {
    amt.value = range.value;
    backdrop.querySelector("#bk-amt-val").textContent = uiMoney(+range.value);
    preview();
  });
  backdrop.querySelectorAll(".bk-term").forEach((b) => b.addEventListener("click", () => {
    term = parseInt(b.dataset.term, 10);
    backdrop.querySelectorAll(".bk-term").forEach((x) => x.classList.toggle("active", x === b));
    preview();
  }));
  const close = () => backdrop.remove();
  backdrop.querySelector("#bk-cancel").addEventListener("click", close);
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
  backdrop.querySelector("#bk-go").addEventListener("click", async () => {
    try {
      const r = await postJSON("/loans/take", { bank_name: bankName, amount: parseFloat(amt.value), term_months: term });
      close(); toast(r.message || "Loan taken."); await window.refreshAll();
    } catch (e) { toast(e.message, true); }
  });
  preview();
}

window.renderBanking = async function (main, snap) {
  const desk = await uiGet("/debt-desk");
  const t = desk.totals;
  const rating = desk.credit.rating;
  main.innerHTML = `
    <div class="sx">
      ${uiHeader(`Banking & Debt — Month ${snap.month}`, "Borrowing is a tool: it buys growth now and costs a fixed payment every month. Keep the payments well inside your cash flow and inside each bank's covenant.",
        `<button type="button" id="bk-paydown">Pay down debt</button>`)}
      ${uiKpis([
        { label: "Total debt", value: uiMoney(t.debt, { compact: true }) },
        { label: "Monthly payments", value: uiMoney(t.monthly_debt_service, { compact: true }),
          sub: t.debt_service_share_of_revenue !== null ? `${t.debt_service_share_of_revenue}% of revenue` : "",
          subTone: t.debt_service_share_of_revenue > 25 ? "neg" : "" },
        { label: "Blended rate", value: t.debt ? uiPct(t.blended_rate * 100, 1) : "—" },
        { label: "Interest coverage", value: t.debt ? `${t.interest_coverage >= 99 ? "99+" : t.interest_coverage.toFixed(1)}x` : "—",
          tone: t.debt && t.interest_coverage < 2 ? "neg" : "", hint: "Operating profit ÷ interest. Below 2x, lenders get nervous." },
        { label: "Debt / equity", value: `${t.debt_to_equity.toFixed(2)}x`, tone: t.debt_to_equity > 1.5 ? "neg" : "" },
        { label: "Credit rating", value: `<span class="sx-pill ${bkRatingTone(rating)}" style="font-size:13px;">${esc(rating)}</span>` },
      ])}
      <div class="sx-grid-2">
        ${uiCard("Your loans", desk.loans.length ? `<div class="bk-loans">${desk.loans.map(bkLoanRow).join("")}</div>`
          : uiEmpty("You're debt-free", "No interest, no covenants — and nothing borrowed to grow faster. The lenders below are ready if you need them."),
          { sub: desk.loans.length ? "Repaid automatically every month, highest rate paid off first if you pay early" : "" })}
        <div style="display:flex; flex-direction:column; gap:18px;">
          ${uiCard("Credit profile", `
            <div class="bk-rating"><span class="sx-pill ${bkRatingTone(rating)}" style="font-size:18px; padding:6px 16px;">${esc(rating)}</span></div>
            ${desk.credit.next ? `<p class="bk-sub" style="margin-top:10px;">To reach <strong>${esc(desk.credit.next)}</strong>: ${esc(desk.credit.needs || "")}</p>` : ""}
            <p class="bk-sub">A better rating lowers every bank's quote. It's driven by debt/equity and by how many times your operating profit covers the interest.</p>`)}
          ${desk.projection.length ? uiCard("How your debt runs down", uiTrendChart([t.debt, ...desk.projection], { labels: [`M${snap.month}`, ...desk.projection.map((_, i) => `M${snap.month + i + 1}`)], height: 140 }),
            { sub: "If you only make the scheduled payments" }) : ""}
        </div>
      </div>
      ${(() => {
        const yes = desk.quotes.filter((q) => q.eligible).sort((a, b) => a.quoted_rate - b.quoted_rate);
        const no = desk.quotes.filter((q) => !q.eligible);
        return uiCard(`Lenders (${desk.quotes.length} banks)`, `
          <div class="bk-lenders">${yes.map((q, i) => bkLenderCard(q, t.debt_to_equity, i === 0)).join("") || uiEmpty("No bank will lend right now", "Bring your debt down or grow equity.")}</div>
          ${no.length ? `<details class="bk-no"><summary>Won't lend to you right now (${no.length})</summary>
            <div class="bk-lenders">${no.map((q) => bkLenderCard(q, t.debt_to_equity, false)).join("")}</div></details>` : ""}`,
          { sub: "Every bank has a character: regional banks favour their home region, specialists favour their industries. Your leverage, credit and the central bank's rate set the price." });
      })()}
      ${desk.closed.length ? `<details class="sx-card"><summary>Repaid and refinanced loans (${desk.closed.length})</summary>
        <table class="sx-table" style="margin-top:10px;">${desk.closed.map((c) => `<tr><td>${esc(c.bank_name)}</td><td class="num">${uiMoney(c.principal)}</td>
          <td class="num">${uiPct(c.interest_rate * 100, 1)}</td><td>${esc(c.status === "paid_off" ? "Repaid" : "Refinanced")}${c.closed_month !== null && c.closed_month !== undefined ? ` in M${c.closed_month}` : ""}</td></tr>`).join("")}</table></details>` : ""}
    </div>`;

  main.querySelectorAll("[data-borrow]").forEach((b) => b.addEventListener("click", () => bkBorrowDrawer(desk, b.dataset.borrow)));
  main.querySelectorAll("[data-refi]").forEach((b) => b.addEventListener("click", async () => {
    const loan = desk.loans.find((l) => l.id === parseInt(b.dataset.refi, 10));
    if (!confirm(`Refinance ${uiMoney(loan.outstanding)} from ${loan.lender} to ${loan.refinance.bank} at ${uiPct(loan.refinance.rate * 100, 1)}?\n\nSaves about ${uiMoney(loan.refinance.annual_saving)} a year; arrangement fee ${uiMoney(loan.refinance.fee)}.`)) return;
    const r = await postJSON("/loans/refinance", { loan_id: loan.id, bank_name: loan.refinance.bank });
    toast(r.message, !r.ok); await window.refreshAll();
  }));
  const payDown = (amount) => uiPlanDrawer({
    eyebrow: "Repay early", title: "Pay down debt",
    description: "Extra repayment goes to your most expensive loan first. It saves interest from next month on, and every dollar repaid is a dollar of cash gone.",
    effect: "Lower monthly payments and interest; better debt/equity.",
    risk: "Cash you repay isn't available for growth or a bad month.",
    input: { kind: "amount", label: "Amount", default: Math.round(amount / 1000) * 1000, min: 1000,
             max: Math.max(1000, Math.round(Math.min(t.debt, Math.max(0, t.cash)) / 1000) * 1000), step: 1000 },
    costFor: (v) => v,
    commitLabel: "Repay",
    commit: async (v) => (await postJSON("/commands/pay-down-debt", { amount: v })).message,
  });
  main.querySelector("#bk-paydown").addEventListener("click", () => (t.debt > 0 ? payDown(Math.min(t.debt, t.cash * 0.1)) : toast("You have no debt to repay.")));
  main.querySelectorAll("[data-payoff]").forEach((b) => b.addEventListener("click", () => payDown(parseFloat(b.dataset.amount))));
};
